import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/NavBar';
import { getRecommendedClubs, getProfile, joinClub } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Target, TrendingUp, MapPin, Clock, Calendar, User, Activity, Zap, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { DotLoader } from 'react-spinners';

function RecommendedClubs() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPaces, setShowPaces] = useState(false);
  const [sortBy, setSortBy] = useState('compatibility'); // 'compatibility' ou 'members'

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate]);

  const fetchData = async () => {
    try {
      const [profileData, clubsData] = await Promise.all([
        getProfile(),
        getRecommendedClubs()
      ]);
      setProfile(profileData);
      setClubs(clubsData);
      setLoading(false);
    } catch (error) {
      console.error('Erreur:', error);
      if (error.response?.status === 400) {
        setError('Veuillez d\'abord configurer votre VDOT dans "Mes Paces"');
      } else {
        setError('Erreur lors du chargement des clubs recommandés');
      }
      setLoading(false);
    }
  };

  const handleJoin = async (clubId) => {
    try {
      await joinClub(clubId);
      fetchData();
      alert('Vous avez rejoint le club avec succès !');
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'inscription');
    }
  };

  const formatPace = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTypeSeanceColor = (typeSeance) => {
    const colors = {
      'Endurance Fondamentale': 'bg-green-900/20 text-green-400 border-green-500',
      'Récupération': 'bg-blue-900/20 text-blue-400 border-blue-500',
      'Seuil/Tempo': 'bg-yellow-900/20 text-yellow-400 border-yellow-500',
      'Fractionné Court': 'bg-red-900/20 text-red-400 border-red-500',
      'Fractionné Long': 'bg-orange-900/20 text-orange-400 border-orange-500',
      'Sortie Longue': 'bg-purple-900/20 text-purple-400 border-purple-500'
    };
    return colors[typeSeance] || 'bg-slate-800 text-gray-400 border-slate-700';
  };

  const getUserPaceForType = (typeSeance) => {
    if (!profile || !profile.paces) return null;
    
    const mapping = {
      'Endurance Fondamentale': (profile.paces.easyMin + profile.paces.easyMax) / 2,
      'Récupération': profile.paces.easyMin,
      'Seuil/Tempo': profile.paces.threshold,
      'Fractionné Court': profile.paces.repetition,
      'Fractionné Long': profile.paces.interval,
      'Sortie Longue': profile.paces.easyMax
    };
    
    return mapping[typeSeance];
  };

  const calculateCompatibility = (club) => {
    if (!profile || !profile.paces || !club.pacesClub || !club.pacesClub.paceMin) {
      return 0;
    }

    const userPace = getUserPaceForType(club.pacesClub.typeSeance);
    if (!userPace) return 0;

    const clubPaceMin = club.pacesClub.paceMin;
    const clubPaceMax = club.pacesClub.paceMax;
    const clubPaceMid = (clubPaceMin + clubPaceMax) / 2;

    const diff = Math.abs(userPace - clubPaceMid);
    const tolerance = (clubPaceMax - clubPaceMin) / 2 + 10;
    
    if (diff <= 5) return 100;
    if (diff <= 10) return 90;
    if (diff <= 15) return 75;
    if (diff <= tolerance) return 60;
    return 40;
  };

  // Trier les clubs
  const sortedClubs = [...clubs].sort((a, b) => {
    if (sortBy === 'compatibility') {
      return calculateCompatibility(b) - calculateCompatibility(a);
    } else {
      return (b.nombreMembres || 0) - (a.nombreMembres || 0);
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <DotLoader color="#6366f1" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-xl p-6 sm:p-8 text-center">
            <div className="bg-indigo-900/30 border border-indigo-500 w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="text-indigo-500" size={40} />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Configuration nécessaire</h2>
            <p className="text-sm sm:text-base text-gray-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/vdot-config')}
              className="w-full sm:w-auto bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-xl transition-all font-semibold shadow-lg shadow-indigo-500/20 active:scale-95"
            >
              Configurer mon VDOT
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-4 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 flex items-center">
            <Sparkles className="mr-2 sm:mr-3 text-indigo-500" size={28} />
            Clubs Recommandés
          </h2>
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-md p-4 sm:p-5 border-l-4 border-l-indigo-500">
            <p className="text-sm sm:text-base text-gray-300">
              Découvrez des clubs parfaitement adaptés à votre niveau et vos paces. Le score de compatibilité est calculé en fonction de vos allures VDOT.
            </p>
          </div>
        </div>

        {/* Mon profil et contrôles */}
        {profile && profile.paces && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-4 sm:p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div className="flex items-center space-x-3">
                <div className="bg-indigo-900/30 border border-indigo-500 p-3 rounded-xl">
                  <Zap className="text-indigo-500" size={24} />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-400">Mon VDOT</p>
                  <p className="text-xl sm:text-2xl font-black text-white">{profile.vdot}</p>
                </div>
              </div>
              
              {/* Toggle pour afficher les paces */}
              <button
                onClick={() => setShowPaces(!showPaces)}
                className="flex items-center justify-between sm:justify-center space-x-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 text-white px-4 py-2 rounded-xl transition-all font-medium"
              >
                <span className="text-sm">Mes allures</span>
                {showPaces ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
            </div>

            {/* Affichage des paces */}
            {showPaces && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3 pt-4 border-t border-slate-800">
                <div className="bg-slate-800 border border-slate-700 p-3 rounded-xl">
                  <p className="text-xs text-gray-400 mb-1">EF</p>
                  <p className="text-sm sm:text-base font-bold text-white">
                    {formatPace(profile.paces.easyMin)} - {formatPace(profile.paces.easyMax)}
                  </p>
                </div>
                <div className="bg-slate-800 border border-slate-700 p-3 rounded-xl">
                  <p className="text-xs text-gray-400 mb-1">Marathon</p>
                  <p className="text-sm sm:text-base font-bold text-white">{formatPace(profile.paces.marathon)}</p>
                </div>
                <div className="bg-slate-800 border border-slate-700 p-3 rounded-xl">
                  <p className="text-xs text-gray-400 mb-1">Seuil</p>
                  <p className="text-sm sm:text-base font-bold text-white">{formatPace(profile.paces.threshold)}</p>
                </div>
                <div className="bg-slate-800 border border-slate-700 p-3 rounded-xl">
                  <p className="text-xs text-gray-400 mb-1">Intervalle</p>
                  <p className="text-sm sm:text-base font-bold text-white">{formatPace(profile.paces.interval)}</p>
                </div>
                <div className="bg-slate-800 border border-slate-700 p-3 rounded-xl col-span-2 sm:col-span-3 md:col-span-1">
                  <p className="text-xs text-gray-400 mb-1">Répétition</p>
                  <p className="text-sm sm:text-base font-bold text-white">{formatPace(profile.paces.repetition)}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Contrôles de tri */}
        <div className="flex items-center justify-between mb-6 bg-slate-900 border border-slate-800 rounded-xl p-3 sm:p-4">
          <p className="text-sm sm:text-base text-gray-400 font-medium">
            {sortedClubs.length} club{sortedClubs.length > 1 ? 's' : ''} trouvé{sortedClubs.length > 1 ? 's' : ''}
          </p>
          <div className="flex items-center space-x-2">
            <span className="text-xs sm:text-sm text-gray-400">Trier par :</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-slate-800 border border-slate-700 text-white px-3 py-1.5 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="compatibility">Compatibilité</option>
              <option value="members">Popularité</option>
            </select>
          </div>
        </div>

        {/* Liste des clubs */}
        {sortedClubs.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {sortedClubs.map((club) => {
              const compatibility = calculateCompatibility(club);
              const userPace = getUserPaceForType(club.pacesClub?.typeSeance);
              
              return (
                <div key={club._id} className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg overflow-hidden hover:border-indigo-500 transition-all">
                  <div className="p-4 sm:p-6">
                    {/* Header du club */}
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4 mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="text-xl sm:text-2xl font-bold text-white truncate">{club.nom}</h3>
                          <span className="bg-indigo-900/30 border border-indigo-500 text-indigo-400 px-2.5 py-1 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap">
                            {club.niveau}
                          </span>
                        </div>
                        <p className="text-sm sm:text-base text-gray-400 line-clamp-2">{club.description}</p>
                      </div>
                      
                      {/* Score de compatibilité */}
                      <div className="flex sm:flex-col items-center sm:items-end gap-3 sm:gap-0">
                        <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center shadow-lg flex-shrink-0 border-2 ${
                          compatibility >= 90 ? 'bg-green-900/30 border-green-500' :
                          compatibility >= 75 ? 'bg-blue-900/30 border-blue-500' :
                          compatibility >= 60 ? 'bg-yellow-900/30 border-yellow-500' :
                          'bg-orange-900/30 border-orange-500'
                        }`}>
                          <div className="text-center">
                            <p className={`text-xl sm:text-2xl font-black ${
                              compatibility >= 90 ? 'text-green-400' :
                              compatibility >= 75 ? 'text-blue-400' :
                              compatibility >= 60 ? 'text-yellow-400' :
                              'text-orange-400'
                            }`}>{compatibility}%</p>
                            <p className="text-[10px] sm:text-xs text-gray-400 font-semibold">Match</p>
                          </div>
                        </div>
                        {compatibility >= 90 && (
                          <Sparkles className="text-yellow-500 animate-pulse hidden sm:block" size={20} />
                        )}
                      </div>
                    </div>

                    {/* Informations du club */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mb-4">
                      <div className="flex items-center bg-slate-800 border border-slate-700 p-2 rounded-lg">
                        <MapPin size={16} className="mr-1.5 text-indigo-500 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-medium text-white truncate">{club.ville}</span>
                      </div>
                      <div className="flex items-center bg-slate-800 border border-slate-700 p-2 rounded-lg">
                        <Clock size={16} className="mr-1.5 text-indigo-500 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-medium text-white truncate">{club.distance}</span>
                      </div>
                      <div className="flex items-center bg-slate-800 border border-slate-700 p-2 rounded-lg col-span-2 sm:col-span-1">
                        <User size={16} className="mr-1.5 text-indigo-500 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-medium text-white">{club.nombreMembres || 0} membres</span>
                      </div>
                    </div>

                    {/* Paces du club */}
                    {club.pacesClub && club.pacesClub.paceMin && (
                      <div className="border-t border-slate-800 pt-4 mb-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                          <div>
                            <p className="text-xs sm:text-sm text-gray-400 mb-1.5">Type de séance principal</p>
                            <span className={`inline-block px-2.5 py-1 rounded-full text-xs sm:text-sm font-bold border ${getTypeSeanceColor(club.pacesClub.typeSeance)}`}>
                              {club.pacesClub.typeSeance}
                            </span>
                          </div>
                          <div className="text-left sm:text-right">
                            <p className="text-xs sm:text-sm text-gray-400 mb-1.5">Allure cible du club</p>
                            <p className="text-lg sm:text-xl font-black text-indigo-400">
                              {formatPace(club.pacesClub.paceMin)} - {formatPace(club.pacesClub.paceMax)} /km
                            </p>
                          </div>
                        </div>

                        {userPace && (
                          <div className="p-3 sm:p-4 bg-slate-800 border border-slate-700 rounded-xl">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <Zap size={16} className="text-indigo-500" />
                                <span className="text-xs sm:text-sm font-bold text-gray-300">Votre allure :</span>
                              </div>
                              <span className="text-base sm:text-lg font-black text-indigo-400">
                                {formatPace(userPace)} /km
                              </span>
                            </div>
                            <div className="mt-2">
                              <div className="h-2 sm:h-2.5 bg-slate-700 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full transition-all ${
                                    compatibility >= 90 ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                                    compatibility >= 75 ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
                                    compatibility >= 60 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                                    'bg-gradient-to-r from-orange-500 to-red-500'
                                  }`}
                                  style={{ width: `${compatibility}%` }}
                                ></div>
                              </div>
                              <p className="text-xs text-gray-400 mt-1.5 font-medium">
                                {compatibility >= 90 ? '🎯 Parfait pour vous !' :
                                 compatibility >= 75 ? '✨ Excellente compatibilité' :
                                 compatibility >= 60 ? '👍 Très bonne compatibilité' :
                                 '✓ Compatible'}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Boutons d'action */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                      <button
                        onClick={() => handleJoin(club._id)}
                        className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white py-2.5 sm:py-3 rounded-xl active:scale-95 transition-all font-bold shadow-lg shadow-indigo-500/20 text-sm sm:text-base"
                      >
                        Rejoindre ce club
                      </button>
                      <button
                        onClick={() => navigate(`/club/${club._id}`)}
                        className="flex-1 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 text-white py-2.5 sm:py-3 rounded-xl active:scale-95 transition-all font-semibold text-sm sm:text-base"
                      >
                        Voir détails
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-xl p-8 sm:p-12 text-center">
            <div className="bg-orange-900/30 border border-orange-500 w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="text-orange-500" size={40} />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3">
              Aucun club compatible trouvé
            </h3>
            <p className="text-sm sm:text-base text-gray-400 mb-6">
              Il n'y a pas encore de clubs avec des paces spécifiques correspondant à votre niveau.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <button
                onClick={() => navigate('/')}
                className="bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 text-white px-6 py-3 rounded-xl active:scale-95 transition-all font-semibold"
              >
                Voir tous les clubs
              </button>
              <button
                onClick={() => navigate('/create')}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-xl active:scale-95 transition-all font-semibold shadow-lg shadow-indigo-500/20"
              >
                Créer un club
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default RecommendedClubs;